import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { useState } from "react";

import { cn } from "@/shared/lib";

import type { DrillAnswer, DrillOption, DrillSpec } from "../../model/Drill";
import { OptionButton, type OptionState } from "./OptionButton";
import { OptionContent } from "./OptionContent";
import { useOptionView } from "./useOptionView";

const PAIRED = msg({ message: "{name}, pair {pair}" });

type PairsBoardProps = {
  spec: DrillSpec;
  answer: DrillAnswer;
  graded: boolean;
  locked: boolean;
  onChange: (answer: DrillAnswer) => void;
};

// Пары: слева враги, справа предметы. Нажми врага, потом предмет — у обоих появится номер
// пары. Повторное нажатие на соединённого врага разрывает пару.
export const PairsBoard = ({ spec, answer, graded, locked, onChange }: PairsBoardProps) => {
  const { i18n, t } = useLingui();
  const view = useOptionView();
  const [selected, setSelected] = useState<string | null>(null);
  const leftIds = new Set(spec.pairs.map((pair) => pair.left_id));
  const left = spec.options.filter((option) => leftIds.has(option.id));
  const right = spec.options.filter((option) => !leftIds.has(option.id));
  const pairOf = (id: string) =>
    answer.pairs.findIndex((pair) => pair.left_id === id || pair.right_id === id);
  const isRightPair = (index: number) => {
    const pair = answer.pairs[index];
    return (
      pair !== undefined &&
      spec.pairs.some((p) => p.left_id === pair.left_id && p.right_id === pair.right_id)
    );
  };
  const stateOf = (id: string): OptionState => {
    const index = pairOf(id);
    if (graded) {
      return index >= 0 && isRightPair(index) ? "right" : "wrong";
    }
    return index >= 0 || selected === id ? "picked" : "idle";
  };
  const pickLeft = (id: string) => {
    if (pairOf(id) >= 0) {
      onChange({ ...answer, pairs: answer.pairs.filter((pair) => pair.left_id !== id) });
      setSelected(id);
      return;
    }
    setSelected(selected === id ? null : id);
  };
  const pickRight = (id: string) => {
    if (selected === null) {
      onChange({ ...answer, pairs: answer.pairs.filter((pair) => pair.right_id !== id) });
      return;
    }
    const rest = answer.pairs.filter((pair) => pair.left_id !== selected && pair.right_id !== id);
    onChange({ ...answer, pairs: [...rest, { left_id: selected, right_id: id }] });
    setSelected(null);
  };
  const column = (options: DrillOption[], onPick: (id: string) => void, heading: string) => (
    <div className="flex flex-col gap-2">
      <span className="text-xs text-subtle">{heading}</span>
      {options.map((option) => {
        const index = pairOf(option.id);
        const optionView = view(option);
        return (
          <OptionButton
            key={option.id}
            state={stateOf(option.id)}
            pressed={index >= 0 || selected === option.id}
            locked={locked}
            label={
              index >= 0
                ? i18n._({ ...PAIRED, values: { name: optionView.name, pair: index + 1 } })
                : optionView.name
            }
            onClick={() => {
              onPick(option.id);
            }}
            className="min-h-15"
          >
            <span
              aria-hidden="true"
              className={cn(
                "inline-flex size-7 shrink-0 items-center justify-center rounded-full font-mono text-sm font-semibold",
                index >= 0 ? "bg-accent text-accent-foreground" : "bg-sidebar-active text-subtle",
              )}
            >
              {index >= 0 ? index + 1 : "·"}
            </span>
            <OptionContent view={optionView} size={40} />
          </OptionButton>
        );
      })}
    </div>
  );
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
      {column(left, pickLeft, t`Enemies`)}
      {column(right, pickRight, t`Items`)}
    </div>
  );
};
